import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth';
import { Wordmark } from '../../components/Wordmark';
import { authHeaders, signInMessageForAuthStatus } from '../../shared/authHeaders';

interface SessionInfo {
  roomCode: string;
  sessionName: string;
  modelName: string | null;
  modelProfileId: string | null;
}

interface ModelSearchResult {
  id: string;
  displayName: string;
  photos?: { url: string }[];
}

export function PublicUploadPage() {
  const { code, roomCode: legacyRoomCode } = useParams<{ code?: string; roomCode?: string }>();
  const [searchParams] = useSearchParams();
  const { getToken, isLoaded: authLoaded, isSignedIn } = useAuth();
  const roomCode = code || legacyRoomCode;
  const uploadToken = searchParams.get('uploadToken')?.trim() || null;
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [modelCreditName, setModelCreditName] = useState('');
  const [modelCreditProfileId, setModelCreditProfileId] = useState<string | null>(null);
  const [modelCreditRemoved, setModelCreditRemoved] = useState(false);
  const [editingModelCredit, setEditingModelCredit] = useState(false);
  const [modelSearchResults, setModelSearchResults] = useState<ModelSearchResult[]>([]);
  const [modelSearchLoading, setModelSearchLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch session info on mount
  useEffect(() => {
    if (!roomCode) return;

    fetch(`/api/rooms/${roomCode}/upload-info`)
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to load session');
        }
        return res.json();
      })
      .then((data: SessionInfo) => {
        setSessionInfo(data);
        setModelCreditName(data.modelName || '');
        setModelCreditProfileId(data.modelProfileId);
        setModelCreditRemoved(!data.modelName);
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [roomCode]);

  useEffect(() => {
    const search = modelCreditName.trim();
    if (!editingModelCredit || search.length < 2) {
      setModelSearchResults([]);
      setModelSearchLoading(false);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setModelSearchLoading(true);
      try {
        const response = await fetch(`/api/profiles?search=${encodeURIComponent(search)}&limit=6`, {
          signal: controller.signal,
        });
        if (response.ok) {
          const data = await response.json();
          setModelSearchResults(data.profiles || []);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Error searching model profiles:', err);
        }
      } finally {
        if (!controller.signal.aborted) {
          setModelSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [editingModelCredit, modelCreditName]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Please select a valid image file (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Image file must be smaller than 10MB');
      return;
    }

    setSelectedFile(file);
    setUploadError(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile) {
      setUploadError('Please select an image');
      return;
    }

    if (!uploadToken && !authLoaded) {
      setUploadError('Checking your sign-in. Please try again in a moment.');
      return;
    }

    if (!uploadToken && !isSignedIn) {
      setUploadError('Sign in with the account you used for this session to upload artwork.');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      if (uploadToken) {
        formData.append('uploadToken', uploadToken);
      }

      const modelName = modelCreditRemoved ? '' : modelCreditName.trim();
      if (modelName) {
        formData.append('modelName', modelName);
      }
      if (!modelCreditRemoved && modelCreditProfileId) {
        formData.append('modelProfileId', modelCreditProfileId);
      }

      const token = uploadToken ? null : await getToken();

      const response = await fetch(`/api/rooms/${roomCode}/artwork`, {
        method: 'POST',
        headers: authHeaders(token),
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        const authMessage = uploadToken
          ? null
          : signInMessageForAuthStatus(response, 'Sign in with the account you used for this session to upload artwork.');
        if (authMessage) throw new Error(authMessage);
        throw new Error(data?.details || data?.error || 'Failed to upload artwork');
      }

      setUploadSuccess(true);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to upload artwork. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadAnother = () => {
    setSelectedFile(null);
    setPreview(null);
    setUploadSuccess(false);
    setUploadError(null);
  };

  const handleModelSearchInput = (value: string) => {
    setModelCreditName(value);
    setModelCreditProfileId(null);
    setModelCreditRemoved(false);
  };

  const handleSelectModelProfile = (profile: ModelSearchResult) => {
    setModelCreditName(profile.displayName);
    setModelCreditProfileId(profile.id);
    setModelCreditRemoved(false);
    setEditingModelCredit(false);
    setModelSearchResults([]);
  };

  const handleRemoveModelCredit = () => {
    setModelCreditName('');
    setModelCreditProfileId(null);
    setModelCreditRemoved(true);
    setEditingModelCredit(false);
    setModelSearchResults([]);
  };

  const modelCreditLabel = modelCreditRemoved || !modelCreditName.trim()
    ? 'No model credit'
    : modelCreditName.trim();
  const eventName = sessionInfo?.sessionName || 'this event';

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-text-dim">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center p-4">
        <div className="bg-panel border border-line rounded-xl p-6 max-w-md w-full text-center shadow-md">
          <h2 className="font-display text-xl font-medium text-warn mb-2">Unable to Upload</h2>
          <p className="text-text-dim mb-4">{error}</p>
          <Link to="/" className="btn">Go to Home</Link>
        </div>
      </div>
    );
  }

  if (uploadSuccess) {
    return (
      <div className="min-h-screen bg-bg flex flex-col">
        {/* Header */}
        <header className="flex-shrink-0 border-b border-line chrome-gradient">
          <div className="page-container h-12 flex items-center">
            <Wordmark className="text-[17px]" />
          </div>
        </header>

        <div className="flex-1 flex flex-col items-center justify-center p-4">
          <div className="bg-panel border border-line rounded-xl p-8 max-w-md w-full text-center shadow-md">
            <div className="w-16 h-16 rounded-full bg-ok/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ok" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
            </div>
            <h2 className="font-display text-xl font-medium text-text mb-2">Artwork Shared!</h2>
            <p className="text-text-dim mb-6">
              Your artwork has been added to the gallery for {eventName}.
            </p>
            <button onClick={handleUploadAnother} className="btn btn-accent w-full">
              Upload Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-line chrome-gradient">
        <div className="page-container h-12 flex items-center">
          <Wordmark className="text-[17px]" />
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <div className="bg-panel border border-line rounded-xl w-full max-w-md shadow-md overflow-hidden">
          {/* Session info header */}
          <div className="px-5 py-4 border-b border-line-soft bg-panel2/50">
            <p className="text-[10px] text-text-faint font-mono uppercase mb-1">
              Gallery upload
            </p>
            <h1 className="font-display text-lg font-medium leading-snug text-text">
              Share Your Art
            </h1>
            <p className="mt-2 text-xs text-text-dim">{eventName}</p>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Image upload area */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                preview
                  ? 'border-transparent'
                  : 'border-line hover:border-text-dim'
              }`}
            >
              {preview ? (
                <div className="relative aspect-[4/3] rounded-lg overflow-hidden">
                  <img src={preview} alt="Preview" className="w-full h-full object-contain bg-tile" />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setPreview(null);
                    }}
                    className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full text-white hover:bg-black/80"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M2 2l8 8M10 2L2 10"/>
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 px-4">
                  <svg className="w-10 h-10 text-text-faint mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="4" width="18" height="16" rx="2"/>
                    <path d="M3 15l5-4 4 3 5-5 4 3"/>
                    <circle cx="8" cy="9" r="1.5"/>
                  </svg>
                  <span className="text-sm text-text-dim">Tap to select an image</span>
                  <span className="text-xs text-text-faint mt-1">JPEG, PNG, WebP or GIF</span>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            <div className="rounded-lg border border-line-soft bg-panel2/45 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase text-text-faint">Model</p>
                  <p className="mt-1 truncate text-sm text-text">{modelCreditLabel}</p>
                  {modelCreditProfileId && !modelCreditRemoved && (
                    <p className="mt-0.5 text-xs text-text-faint">Linked platform profile</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setEditingModelCredit((open) => !open);
                    if (modelCreditRemoved) {
                      setModelCreditRemoved(false);
                    }
                  }}
                  className="btn btn-sm flex-shrink-0"
                >
                  {editingModelCredit ? 'Close' : 'Change'}
                </button>
              </div>

              {editingModelCredit && (
                <div className="mt-3 space-y-2">
                  <input
                    type="text"
                    value={modelCreditName}
                    onChange={(e) => handleModelSearchInput(e.target.value)}
                    placeholder="Search models or type a credit"
                    className="w-full h-10 px-3 bg-bg border border-line rounded-lg text-sm text-text placeholder-text-faint outline-none focus:border-text/30 transition-colors"
                  />

                  {(modelSearchLoading || modelSearchResults.length > 0) && (
                    <div className="max-h-44 overflow-y-auto rounded-lg border border-line-soft bg-bg">
                      {modelSearchLoading ? (
                        <div className="px-3 py-2 text-sm text-text-dim">Searching...</div>
                      ) : (
                        modelSearchResults.map((profile) => (
                          <button
                            key={profile.id}
                            type="button"
                            onClick={() => handleSelectModelProfile(profile)}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-panel2"
                          >
                            {profile.photos?.[0]?.url ? (
                              <img
                                src={profile.photos[0].url}
                                alt={profile.displayName}
                                className="h-7 w-7 rounded-full object-cover"
                              />
                            ) : (
                              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-panel text-xs text-text-dim">
                                {profile.displayName[0]?.toUpperCase() || '?'}
                              </span>
                            )}
                            <span className="min-w-0 truncate text-sm text-text">{profile.displayName}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setEditingModelCredit(false)}
                      className="btn btn-sm"
                    >
                      Use This Credit
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveModelCredit}
                      className="btn btn-sm"
                    >
                      Remove Credit
                    </button>
                  </div>
                </div>
              )}
            </div>

            {uploadError && (
              <p className="text-sm text-warn">{uploadError}</p>
            )}
            {authLoaded && !isSignedIn && !uploadToken && !uploadError && (
              <p className="text-sm text-warn">
                Sign in with the account you used for this session to upload artwork.
              </p>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={!selectedFile || isUploading}
              className="w-full h-11 bg-accent rounded-lg text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent/90 transition-colors"
            >
              {isUploading ? 'Uploading...' : 'Share'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
